import { Dispatch } from 'redux';
import getHomeSpaces from '@core/api/spaces/getHomeSpaces';
import getHomeSpacesProgress from '@core/api/materials/getHomeSpacesProgress';
import getHomeSpacesEngagement from '@core/api/engagement/getHomeSpacesEngagement';
import {
  HOME_FETCH_SPACES_FAILURE,
  HOME_FETCH_SPACES_REQUEST,
  HOME_FETCH_SPACES_SUCCESS,
} from '@core/constants/actions';
import { getHomeViewRole } from '@app/selectors/home';

const HOME_SPACES_PAGE_SIZE = 50;

const getHomeSpacesErrorType = (error: unknown): 'network' | 'api' => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'network';
  }

  const message = error instanceof Error ? error.message : String(error ?? '');

  if (
    message === 'Network Error' ||
    message.includes('Failed to fetch') ||
    message.includes('Network request failed')
  ) {
    return 'network';
  }

  return 'api';
};

export const fetchHomeSpaces =
  () => async (dispatch: Dispatch, getState: () => any) => {
    const state = getState();
    const viewRole = getHomeViewRole(state);
    const roleState = state.home.spacesByRole[viewRole];

    if (roleState.loading || !roleState.hasMore) {
      return;
    }

    dispatch({ type: HOME_FETCH_SPACES_REQUEST, role: viewRole });

    try {
      const { homeSpaces, nextCursor } = await getHomeSpaces({
        limit: HOME_SPACES_PAGE_SIZE,
        cursor: roleState.cursor,
        role: viewRole,
      });

      const spaceIds = homeSpaces.map((space) => space.spaceId);
      const educatorIds = homeSpaces
        .filter((space) => space.role === 'educator')
        .map((space) => space.spaceId);

      const [progressResponse, engagementResponse] = await Promise.all([
        getHomeSpacesProgress(spaceIds),
        getHomeSpacesEngagement(educatorIds),
      ]);

      dispatch({
        type: HOME_FETCH_SPACES_SUCCESS,
        role: viewRole,
        spaces: homeSpaces,
        progress: progressResponse.spaceProgress,
        engagement: engagementResponse.spaceEngagement,
        cursor: nextCursor,
        hasMore: nextCursor !== null,
      });
    } catch (error) {
      console.error('Failed to fetch home spaces', error);
      dispatch({
        type: HOME_FETCH_SPACES_FAILURE,
        role: viewRole,
        errorType: getHomeSpacesErrorType(error),
      });
    }
  };
